import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { dailyDriverRecordsApi, driversApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  Trash2,
  Edit,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const DailyDriverRecords = () => {
  const [records, setRecords] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [yearlySummary, setYearlySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedDriverFilter, setSelectedDriverFilter] = useState('all');

  const [formData, setFormData] = useState({
    driver_id: '',
    date: new Date().toISOString().split('T')[0],
    revenue: '',
    expense: '',
    expense_description: '',
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, recordsRes, monthlyRes, yearlyRes] = await Promise.all([
        driversApi.getAll(),
        dailyDriverRecordsApi.getAll({ year: selectedYear, month: selectedMonth }),
        dailyDriverRecordsApi.getMonthlySummary(selectedYear, selectedMonth),
        dailyDriverRecordsApi.getYearlySummary(selectedYear),
      ]);
      setDrivers(driversRes.data);
      setRecords(recordsRes.data);
      setMonthlySummary(monthlyRes.data);
      setYearlySummary(yearlyRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.driver_id || !formData.date) {
      toast.error('Please select a driver and date');
      return;
    }

    try {
      const payload = {
        ...formData,
        revenue: parseFloat(formData.revenue) || 0,
        expense: parseFloat(formData.expense) || 0,
      };

      if (editingRecord) {
        await dailyDriverRecordsApi.update(editingRecord.id, payload);
        toast.success('Record updated successfully');
      } else {
        await dailyDriverRecordsApi.create(payload);
        toast.success('Record added successfully');
      }
      setIsDialogOpen(false);
      setEditingRecord(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      await dailyDriverRecordsApi.delete(id);
      toast.success('Record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      driver_id: record.driver_id,
      date: record.date,
      revenue: record.revenue.toString(),
      expense: record.expense.toString(),
      expense_description: record.expense_description || '',
      notes: record.notes || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      driver_id: '',
      date: new Date().toISOString().split('T')[0],
      revenue: '',
      expense: '',
      expense_description: '',
      notes: '',
    });
    setEditingRecord(null);
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || 'Unknown';
  };

  const filteredRecords = selectedDriverFilter === 'all' 
    ? records 
    : records.filter(r => r.driver_id === selectedDriverFilter);

  if (loading && !records.length) {
    return (
      <Layout title="Daily Driver Records">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Daily Driver Records"
      subtitle="Track daily revenue & expenses per driver (OPEX)"
      actions={
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="add-daily-record-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Daily Record
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingRecord ? 'Edit Record' : 'Add Daily Record'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Driver *</Label>
                <Select value={formData.driver_id} onValueChange={(v) => setFormData({...formData, driver_id: v})}>
                  <SelectTrigger data-testid="select-driver">
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  data-testid="input-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Revenue (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.revenue}
                    onChange={(e) => setFormData({...formData, revenue: e.target.value})}
                    data-testid="input-revenue"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expense (₹) - OPEX</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.expense}
                    onChange={(e) => setFormData({...formData, expense: e.target.value})}
                    data-testid="input-expense"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Expense Description</Label>
                <Input
                  placeholder="Fuel, maintenance, etc."
                  value={formData.expense_description}
                  onChange={(e) => setFormData({...formData, expense_description: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Net Profit:</span>
                  <span className={`font-bold ${(parseFloat(formData.revenue) || 0) - (parseFloat(formData.expense) || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency((parseFloat(formData.revenue) || 0) - (parseFloat(formData.expense) || 0))}
                  </span>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                {editingRecord ? 'Update Record' : 'Add Record'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="daily-driver-records-page">
        {/* Period Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500">Year:</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500">Month:</Label>
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500">Driver:</Label>
                <Select value={selectedDriverFilter} onValueChange={setSelectedDriverFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Drivers</SelectItem>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Tabs defaultValue="monthly" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="monthly">Monthly Summary</TabsTrigger>
            <TabsTrigger value="yearly">Yearly Summary</TabsTrigger>
          </TabsList>
          
          <TabsContent value="monthly">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Monthly Revenue</p>
                      <p className="text-xl font-bold text-emerald-600 tabular-nums">
                        {formatCurrency(monthlySummary?.total_revenue)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Monthly Expense (OPEX)</p>
                      <p className="text-xl font-bold text-red-600 tabular-nums">
                        {formatCurrency(monthlySummary?.total_expense)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={`${(monthlySummary?.total_net_profit || 0) >= 0 ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-red-100 border-red-300'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(monthlySummary?.total_net_profit || 0) >= 0 ? 'bg-[#D4AF37]/20' : 'bg-red-200'}`}>
                      <IndianRupee className={`w-5 h-5 ${(monthlySummary?.total_net_profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Monthly Net Profit</p>
                      <p className={`text-xl font-bold tabular-nums ${(monthlySummary?.total_net_profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`}>
                        {formatCurrency(monthlySummary?.total_net_profit)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Active Drivers</p>
                      <p className="text-xl font-bold text-blue-600 tabular-nums">
                        {monthlySummary?.drivers?.length || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Driver Breakdown */}
            {monthlySummary?.drivers?.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Monthly Driver Performance - {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Driver</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Expense</TableHead>
                        <TableHead className="text-right">Net Profit</TableHead>
                        <TableHead className="text-right">Records</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlySummary.drivers.map((d) => (
                        <TableRow key={d.driver_id}>
                          <TableCell className="font-medium">{d.driver_name}</TableCell>
                          <TableCell className="text-right text-emerald-600 tabular-nums">{formatCurrency(d.total_revenue)}</TableCell>
                          <TableCell className="text-right text-red-600 tabular-nums">{formatCurrency(d.total_expense)}</TableCell>
                          <TableCell className={`text-right font-bold tabular-nums ${d.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            <div className="flex items-center justify-end gap-1">
                              {d.net_profit >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                              {formatCurrency(d.net_profit)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{d.record_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="yearly">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Yearly Revenue</p>
                      <p className="text-xl font-bold text-emerald-600 tabular-nums">
                        {formatCurrency(yearlySummary?.total_revenue)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Yearly Expense (OPEX)</p>
                      <p className="text-xl font-bold text-red-600 tabular-nums">
                        {formatCurrency(yearlySummary?.total_expense)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={`${(yearlySummary?.total_net_profit || 0) >= 0 ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-red-100 border-red-300'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(yearlySummary?.total_net_profit || 0) >= 0 ? 'bg-[#D4AF37]/20' : 'bg-red-200'}`}>
                      <IndianRupee className={`w-5 h-5 ${(yearlySummary?.total_net_profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Yearly Net Profit</p>
                      <p className={`text-xl font-bold tabular-nums ${(yearlySummary?.total_net_profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`}>
                        {formatCurrency(yearlySummary?.total_net_profit)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Year</p>
                      <p className="text-xl font-bold text-blue-600 tabular-nums">
                        {selectedYear}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Yearly Driver Breakdown */}
            {yearlySummary?.drivers?.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Yearly Driver Performance - {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Driver</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Expense</TableHead>
                        <TableHead className="text-right">Net Profit</TableHead>
                        <TableHead className="text-right">Records</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {yearlySummary.drivers.map((d) => (
                        <TableRow key={d.driver_id}>
                          <TableCell className="font-medium">{d.driver_name}</TableCell>
                          <TableCell className="text-right text-emerald-600 tabular-nums">{formatCurrency(d.total_revenue)}</TableCell>
                          <TableCell className="text-right text-red-600 tabular-nums">{formatCurrency(d.total_expense)}</TableCell>
                          <TableCell className={`text-right font-bold tabular-nums ${d.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            <div className="flex items-center justify-end gap-1">
                              {d.net_profit >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                              {formatCurrency(d.net_profit)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{d.record_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Daily Records Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Daily Records - {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRecords.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No records found for this period</p>
                <p className="text-sm mt-1">Click "Add Daily Record" to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Expense</TableHead>
                    <TableHead className="text-right">Net Profit</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.date}</TableCell>
                      <TableCell>{getDriverName(record.driver_id)}</TableCell>
                      <TableCell className="text-right text-emerald-600 tabular-nums">{formatCurrency(record.revenue)}</TableCell>
                      <TableCell className="text-right text-red-600 tabular-nums">{formatCurrency(record.expense)}</TableCell>
                      <TableCell className={`text-right font-bold tabular-nums ${record.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(record.net_profit)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-slate-500">
                        {record.expense_description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(record)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(record.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DailyDriverRecords;
